import { CircleMinus, Pencil, TriangleAlert, Undo2 } from 'lucide-react';

import { RowMenu } from '@/ui/RowMenu';
import { AXIS_LABEL, edited, type Draft } from './dimensionsDraft';
import type { DimensionsState } from './useDimensionsSection';

function NameCell({ row, was }: { row: Draft; was: Draft | undefined }) {
  return (
    <td>
      <b className="dim-name">{row.label}</b>
      {row.retire && <span className="hint dim-retiring">Will be retired</span>}
      {!row.key && <span className="badge badge-info dim-state">New</span>}
      {!row.retire && edited(row, was) && <span className="badge badge-neutral dim-state">Edited</span>}
    </td>
  );
}

function ActionsCell({ row, state }: { row: Draft; state: DimensionsState }) {
  return (
    <td className="row-actions-cell">
      {row.retire ? (
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          data-tooltip="Keep"
          aria-label={`Keep ${row.label}`}
          onClick={() => state.update(row.id, { retire: false })}
        >
          <Undo2 aria-hidden="true" />
        </button>
      ) : (
        <RowMenu
          label={`Actions for ${row.label}`}
          items={[
            { label: 'Edit', icon: <Pencil aria-hidden="true" />, onSelect: () => state.setEditing(row) },
            { label: row.key ? 'Retire' : 'Remove', icon: <CircleMinus aria-hidden="true" />, onSelect: () => state.discard(row) },
          ]}
        />
      )}
    </td>
  );
}

function DimensionTableRow({ row, state }: { row: Draft; state: DimensionsState }) {
  return (
    <tr className={row.retire ? 'is-retiring' : undefined}>
      <NameCell row={row} was={state.before.get(row.id)} />
      <td className="dim-question">{row.description}</td>
      <td className="num">{row.retire ? <span className="hint">Not counted</span> : `${row.weight}%`}</td>
      <td>{row.axis ? AXIS_LABEL[row.axis] : <span className="hint">Neither</span>}</td>
      {state.editable && <ActionsCell row={row} state={state} />}
    </tr>
  );
}

function TotalRow({ total, editable }: { total: number; editable: boolean }) {
  return (
    <tr className={`weights-total-row ${total === 100 ? 'is-balanced' : 'is-off'}`}>
      <th scope="row">
        <span className="sr-only">Total</span>
      </th>
      <td className="weights-total-note-cell" aria-live="polite">
        {total !== 100 && (
          <span className="weights-total-note">
            <TriangleAlert aria-hidden="true" /> {Math.abs(total - 100)}% {total > 100 ? 'over' : 'short'}: the weights must add up to 100%
          </span>
        )}
      </td>
      <td className="num weights-total-value">{total}%</td>
      <td colSpan={editable ? 2 : 1} />
    </tr>
  );
}

export function DimensionsTable({ state }: { state: DimensionsState }) {
  return (
    <div className="table-scroll">
      <table className="data dimensions-table">
        <thead>
          <tr>
            <th scope="col" className="dim-label-col">Dimension</th>
            <th scope="col">Question for the AI</th>
            <th scope="col" className="num dim-weight-col">Weight</th>
            <th scope="col" className="dim-axis-col">Matrix axis</th>
            {state.editable && <th scope="col"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {state.draft.map((row) => (
            <DimensionTableRow key={row.id} row={row} state={state} />
          ))}
        </tbody>
        <tfoot>
          <TotalRow total={state.total} editable={state.editable} />
        </tfoot>
      </table>
    </div>
  );
}
